import { Dimensions, SpreadGroundImage } from 'editor/src/store/design/types';

import { getSpreadGroundScales } from 'editor/src/util/getSpreadGroundImageScale';

// Viewport translate limits on one axis, named as useGetCenter names them: `min` is the
// largest allowed translate, `max` the smallest.
export interface PanBounds {
  min: number;
  max: number;
}

// The extent of the background/foreground mockup on one axis, in unzoomed canvas px.
export interface GroundExtent {
  start: number;
  size: number;
}

// Gap kept between the mockup edge and the viewport edge once the view is panned to it.
export const GROUND_PAN_MARGIN = 20;

/**
 * Widens a pan range so every part of the mockup can be brought into view.
 * The spread-only range stops at the print area, which leaves the parts of a
 * garment outside it (collar, hem) cropped and unreachable once zoomed in.
 *
 * A mockup larger than the viewport can be panned edge to edge. One that fits
 * keeps the spread's range when that range already shows it whole; when the
 * print area sits off-centre on the mockup and the range would clip it, the
 * range is stretched just far enough to show the whole mockup.
 */
export function extendPanBoundsToGround(
  bounds: PanBounds,
  ground: GroundExtent,
  zoom: number,
  viewportSize: number,
): PanBounds {
  // screen = world * zoom + translate
  const startAtMargin = GROUND_PAN_MARGIN - ground.start * zoom;
  const endAtMargin = viewportSize - GROUND_PAN_MARGIN - (ground.start + ground.size) * zoom;

  if (endAtMargin < startAtMargin) {
    // Larger than the viewport: from its start edge at the margin to its end edge at the margin.
    return {
      min: Math.max(bounds.min, startAtMargin),
      max: Math.min(bounds.max, endAtMargin),
    };
  }

  // It fits, and any translate from startAtMargin up to endAtMargin shows it whole.
  // Reach that window when the spread's range lies entirely to one side of it.
  if (bounds.max > endAtMargin) {
    return { min: bounds.min, max: endAtMargin };
  }
  if (bounds.min < startAtMargin) {
    return { min: startAtMargin, max: bounds.max };
  }
  return bounds;
}

/**
 * Where the mockup sits around the spread, in unzoomed canvas px, or undefined
 * when the spread has neither a background nor a foreground.
 */
export function getGroundExtents(
  background: SpreadGroundImage | undefined,
  foreground: SpreadGroundImage | undefined,
  spreadLeft: number,
  spreadTop: number,
  spreadDim: Dimensions,
): { x: GroundExtent; y: GroundExtent } | undefined {
  if (!background && !foreground) {
    return undefined;
  }

  // Each layer's rect exactly as the canvas draws it (SpreadGroundImageElement), then
  // their union, so neither layer's far edge is left out of reach.
  const layers = [background, foreground]
    .filter((layer): layer is SpreadGroundImage => !!layer)
    .map((layer) => {
      const { scaleX, scaleY } = getSpreadGroundScales(layer, spreadDim);
      return {
        left: spreadLeft - layer.left * scaleX,
        top: spreadTop - layer.top * scaleY,
        width: layer.imgWidth * scaleX,
        height: layer.imgHeight * scaleY,
      };
    });

  const left = Math.min(spreadLeft, ...layers.map((layer) => layer.left));
  const top = Math.min(spreadTop, ...layers.map((layer) => layer.top));
  const right = Math.max(spreadLeft + spreadDim.width, ...layers.map((layer) => layer.left + layer.width));
  const bottom = Math.max(spreadTop + spreadDim.height, ...layers.map((layer) => layer.top + layer.height));

  return {
    x: { start: left, size: right - left },
    y: { start: top, size: bottom - top },
  };
}
